import { Button, Flex, Heading, Text } from "@radix-ui/themes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { NavLink, Navigate } from "react-router-dom";
import { routes } from "../../../routes";
import { getAccountProfile } from "../../../shared/api/nest-api";
import { AuthRoot } from "../auth-root.component";
import { authClient } from "../client/auth-client";
import { AuthErrorMessage } from "../components/auth-error-message.component";

const formatAddress = (address: {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode?: string;
}): string => {
  const countryLabel =
    address.countryCode === "DE" ? "Deutschland" : address.countryCode;

  return `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}${countryLabel ? `, ${countryLabel}` : ""}`;
};

const MISSING_VALUE_LABEL = "Keine Angabe";

export function AccountPage() {
  const sessionQuery = authClient.useSession();
  const profileQuery = useQuery({
    queryKey: ["auth", "profile"],
    queryFn: getAccountProfile,
  });
  const signOutMutation = useMutation({
    mutationFn: async () => {
      await authClient.signOut();
    },
  });
  const phoneNumberLabel = profileQuery.isPending
    ? "Wird geladen..."
    : (profileQuery.data?.phoneNumber ?? MISSING_VALUE_LABEL);
  const addressLabel = profileQuery.isPending
    ? "Wird geladen..."
    : profileQuery.data?.address
      ? formatAddress(profileQuery.data.address)
      : MISSING_VALUE_LABEL;

  return (
    <AuthRoot title="Konto">
      <Heading as="h2" size="4">
        Eingeloggt
      </Heading>
      <Text size="2">
        E-Mail: {sessionQuery.data?.user.email ?? "Wird geladen..."}
      </Text>
      <Text size="2">
        Status:{" "}
        {sessionQuery.data?.user.emailVerified
          ? "Verifiziert"
          : "Nicht verifiziert"}
      </Text>
      <Text size="2">Telefonnummer: {phoneNumberLabel}</Text>
      <Text size="2">Adresse: {addressLabel}</Text>
      <Flex direction="column" gap="2">
        <Button
          size="3"
          variant="outline"
          loading={signOutMutation.isPending}
          onClick={() => {
            signOutMutation.mutate();
          }}
        >
          Ausloggen
        </Button>
        <Button size="3" variant="soft" asChild>
          <NavLink to={routes.home}>Zur Startseite</NavLink>
        </Button>
      </Flex>
      {signOutMutation.isSuccess && <Navigate to={routes.auth.login} replace />}
      {signOutMutation.error && (
        <AuthErrorMessage error={signOutMutation.error} />
      )}
      {profileQuery.error && <AuthErrorMessage error={profileQuery.error} />}
    </AuthRoot>
  );
}
